'use client';

/**
 * NotificationItem Component
 *
 * Renders a single notification with icon, content, timestamp, and actions.
 * Supports click-to-navigate and swipe-to-archive gestures.
 */

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight, X, Bell, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveIcon } from '@/lib/navigation/icons';
import { colorSchemes, type NavColorScheme } from '@/lib/ui/colorSchemes';
import type { Notification } from '@/store/slices/notificationsTypes';
import { getCategoryMeta, getPageLabel, getPageIcon } from '@/store/slices/notificationsTypes';

// ============================================================================
// Color Scheme Helpers
// ============================================================================

/** Map category color names to valid NavColorScheme keys */
function getCategoryColorScheme(color: string | undefined): NavColorScheme {
  // Direct mapping for supported schemes
  const validSchemes: NavColorScheme[] = [
    'cyan', 'amber', 'emerald', 'blue', 'purple', 'violet', 'green',
  ];
  if (color && validSchemes.includes(color as NavColorScheme)) {
    return color as NavColorScheme;
  }
  // Legacy behavior: rose/red → blue
  if (color === 'rose' || color === 'red') return 'blue';
  return 'cyan';
}

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onArchive?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
  /** Current page path for showing page badges on notifications from different pages */
  currentPage?: string | null;
}

export function NotificationItem({
  notification,
  onMarkRead,
  onArchive,
  onClick,
  compact = false,
  currentPage,
}: NotificationItemProps) {
  const categoryMeta = getCategoryMeta(notification.category);
  const schemeName = getCategoryColorScheme(categoryMeta?.color);
  const colors = colorSchemes[schemeName];

  // Get the icon component dynamically using shared resolver
  const iconName = notification.icon || categoryMeta?.icon || 'Bell';
  const IconComponent = resolveIcon(iconName) || Bell;

  // Guard formatDistanceToNow behind a mounted check to avoid SSR/client
  // hydration mismatch (#418). Relative timestamps differ between server and
  // client render times, so we render a static placeholder until mounted.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const timeAgo = mounted
    ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
    : '';

  const handleClick = (e: React.MouseEvent) => {
    // Mark as read on click if not already read
    if (!notification.isRead && onMarkRead) {
      onMarkRead(notification.id);
    }

    // Call custom onClick handler
    if (onClick) {
      onClick(notification);
    }
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onArchive) {
      onArchive(notification.id);
    }
  };

  // Determine if we should show a page badge (notification from different page)
  const showPageBadge = (() => {
    // No badge if no source page or current page
    if (!notification.sourcePage) return false;
    if (!currentPage) return true; // Always show if we're on the notifications page

    // Show badge if source page doesn't match current page
    // Handle prefix matching (e.g., '/calls/123' should match '/calls')
    const normalizePagePath = (path: string) => {
      const parts = path.split('/').filter(Boolean);
      return '/' + (parts[0] || '');
    };

    return normalizePagePath(notification.sourcePage) !== normalizePagePath(currentPage);
  })();

  // Get the page badge icon component
  const PageBadgeIcon = showPageBadge
    ? resolveIcon(getPageIcon(notification.sourcePage)) || ExternalLink
    : null;

  const isActionable = notification.behavior !== 'informational';
  // TODO: surface cost-spike notifications when cost-alert kind is added (would render a "$X cost spike" badge here, sourced from notification.metadata)

  const content = (
    <div
      className={cn(
        'group flex items-start gap-3 px-4 py-3 transition-all duration-200',
        'hover:bg-white/[0.04] cursor-pointer',
        !notification.isRead && 'bg-white/[0.02]',
        // Actionable unread notifications get an accent border
        isActionable && !notification.isRead && 'border-l-2 border-amber-500/60',
        // Informational get no accent
        !isActionable && 'border-l-2 border-transparent'
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 p-2 rounded-lg transition-transform duration-200',
          'group-hover:scale-105',
          colors.iconBg
        )}
      >
        <IconComponent className={cn('w-4 h-4', colors.iconColor, !isActionable && 'opacity-60')} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Title with unread indicator */}
            <div className="flex items-start gap-2">
              <span
                className={cn(
                  'text-sm font-medium break-words',
                  notification.isRead ? 'text-white/70' : 'text-white'
                )}
              >
                {notification.title}
              </span>
              {!notification.isRead && isActionable && (
                <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
              )}
            </div>

            {/* Body (hide in compact mode) */}
            {!compact && (
              <p className="mt-0.5 text-xs text-white/70 break-words">
                {notification.body}
              </p>
            )}

            {/* Page Badge - shows when notification is from a different page */}
            {showPageBadge && notification.sourcePage && !compact && (
              <Link
                href={notification.sourcePage}
                target="_blank"
                rel="noopener"
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  'inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-xs rounded-full',
                  'bg-white/[0.06] border border-white/[0.08]',
                  'text-white/70 hover:text-white hover:bg-white/[0.1]',
                  'transition-colors'
                )}
              >
                {PageBadgeIcon && <PageBadgeIcon className="w-3 h-3" />}
                <span>From: {getPageLabel(notification.sourcePage)}</span>
                <ExternalLink className="w-2.5 h-2.5 ml-0.5 opacity-50" />
              </Link>
            )}
          </div>

          {/* Timestamp — suppressHydrationWarning because the relative
              time string is empty on the server and populated after mount (#418) */}
          <span
            className={cn(
              'flex-shrink-0 text-xs whitespace-nowrap',
              notification.isRead ? 'text-white/60' : colors.iconColor
            )}
            suppressHydrationWarning
          >
            {timeAgo}
          </span>
        </div>

        {/* Action button (if present) */}
        {notification.actionLabel && notification.actionUrl && !compact && (
          <div className="mt-2">
            <Link
              href={notification.actionUrl}
              target="_blank"
              rel="noopener"
              className={cn(
                'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded',
                'transition-colors',
                colors.iconBg,
                colors.iconColor,
                'hover:opacity-80'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {notification.actionLabel}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>

      {/* Archive button (visible on hover) */}
      {onArchive && (
        <button
          onClick={handleArchive}
          className={cn(
            'flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100',
            'text-white/70 hover:text-white hover:bg-white/[0.08]',
            'transition-all duration-200'
          )}
          title="Archive"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  // Wrap in Link if actionUrl exists (opens in new tab to preserve current page)
  if (notification.actionUrl && !onClick) {
    return (
      <Link href={notification.actionUrl} target="_blank" rel="noopener" className="block">
        {content}
      </Link>
    );
  }

  return content;
}

export default NotificationItem;
